import { api, state, openInspector } from '../state.js';
import { esc, formatDateTime, badge } from '/core/js/utils.js';
import { mountPageHeader, errorState } from './ui/index.js';

/**
 * The privileged-write trail: every change, who made it and from where.
 *
 * `detail` is shown as-is. The writer redacts credential-shaped keys on the way
 * in, so what is stored is what is safe to display — this page does no
 * filtering of its own, because a filter here would create the illusion that
 * unsafe values are handled when they had already been written.
 *
 * Filters (U8): a Community (then the rows come from that community's
 * activity endpoint, which knows the ways an entry names a community), a
 * family of actions as chips, the outcome, and a search over who and what.
 * The filters live in the hash (`#/audit?site=…&family=…`) so a filtered view
 * is a link — the community home's "Audit log" is one.
 *
 * Layout: the whole window (.page-wide); every column on one line except
 * detail, which takes the rest and is cut short — a click on the row opens
 * the whole entry in the drawer.
 */
export const FAMILIES = [
  { key: 'lots',     label: 'Lots & files', prefixes: ['arc.feature.', 'arc.features.', 'arc.file.', 'arc.files.', 'arc.filecategory.'] },
  { key: 'palettes', label: 'Palettes',     prefixes: ['arc.palette.'] },
  { key: 'builders', label: 'Builders',     prefixes: ['arc.builder.', 'arc.floorplan.', 'arc.elevation.'] },
  { key: 'config',   label: 'Config',       prefixes: ['arc.map.', 'arc.layer.', 'arc.site.', 'arc.form.', 'arc.attribute.'] },
  { key: 'platform', label: 'People & platform', exclude: true },     // everything else: users and their access, sign-in, settings
];
const LIMIT = 200;

class ArcAudit extends HTMLElement {
  #items = [];
  #filter = { site: '', family: '', outcome: '', q: '' };
  #seq = 0;           // drops a slow reply that a newer filter overtook

  connectedCallback() {
    this.classList.add('page-wide');
    const q = this.ctx?.query ?? {};
    this.#filter = { site: q.site ?? '', family: FAMILIES.some(f => f.key === q.family) ? q.family : '', outcome: ['ok', 'failed'].includes(q.outcome) ? q.outcome : '', q: q.q ?? '' };
    this.innerHTML = `
      <div data-slot="header"></div>
      <arc-toolbar></arc-toolbar>
      <div class="audit-families" role="group" aria-label="Kind of change">
        <button type="button" class="chip chip-btn" data-family="" aria-pressed="false">All</button>
        ${FAMILIES.map(f => `<button type="button" class="chip chip-btn" data-family="${f.key}" aria-pressed="false">${esc(f.label)}</button>`).join('')}
      </div>
      <div class="card"><arc-data-table></arc-data-table></div>`;
    mountPageHeader(this.querySelector('[data-slot="header"]'), {
      title: 'Audit log', context: 'System',
      sub: 'Every privileged write: who did what, to what, when and from where. The newest 200 that match.',
    });
    this.querySelector('arc-toolbar').configure({
      search: { placeholder: 'Who or what', value: this.#filter.q },
      filters: [
        { name: 'site', label: 'Community', all: 'All communities', options: (state.sites ?? []).map(s => ({ value: s.slug, label: s.name })), value: this.#filter.site },
        { name: 'outcome', label: 'Outcome', all: 'All outcomes', options: [{ value: 'ok', label: 'Succeeded' }, { value: 'failed', label: 'Failed' }], value: this.#filter.outcome },
      ],
      count: '',
    });
    this.addEventListener('change', e => {
      if (!e.target.matches('arc-toolbar')) return;
      this.#filter = { ...this.#filter, q: e.detail.search, site: e.detail.filters.site ?? '', outcome: e.detail.filters.outcome ?? '' };
      this.#load();
    });
    this.querySelector('.audit-families').addEventListener('click', e => {
      const b = e.target.closest('[data-family]');
      if (!b) return;
      this.#filter.family = b.dataset.family;
      this.#load();
    });
    this.addEventListener('row-click', e => this.#open(e.detail.row));
    this.#syncChips();
    this.querySelector('arc-data-table').loading = true;
    this.#load();
  }

  #syncChips() {
    for (const b of this.querySelectorAll('.audit-families [data-family]')) b.setAttribute('aria-pressed', String(b.dataset.family === this.#filter.family));
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(this.#filter)) if (v) qs.set(k, v);
    const want = `#/audit${qs.size ? `?${qs}` : ''}`;
    if (location.hash !== want) history.replaceState(null, '', `${location.pathname}${want}`);
  }

  /** The query the filters amount to, in the audit API's terms. */
  #params() {
    const p = { limit: LIMIT };
    if (this.#filter.q) p.q = this.#filter.q;
    if (this.#filter.outcome) p.outcome = this.#filter.outcome;
    const fam = FAMILIES.find(f => f.key === this.#filter.family);
    if (fam?.exclude) p.exclude_prefix = FAMILIES.flatMap(f => f.prefixes ?? []).join(',');
    else if (fam) p.action_prefix = fam.prefixes.join(',');
    return p;
  }

  async #load() {
    const seq = ++this.#seq;
    this.#syncChips();
    const site = this.#filter.site ? (state.sites ?? []).find(s => s.slug === this.#filter.site) : null;
    try {
      const res = site ? await api.sites.activity(site.id, this.#params()) : await api.audit.list(this.#params());
      if (seq !== this.#seq) return;
      this.#items = (res.items ?? []).map(r => ({ ...r, detail_obj: ArcAudit.#parse(r.detail) }));
      this.#render();
    } catch (err) {
      if (seq !== this.#seq) return;
      this.querySelector('.card').innerHTML = errorState(err, 'Could not load the audit log');
    }
  }

  static #parse(detail) {
    if (detail && typeof detail === 'object') return detail;
    if (typeof detail === 'string') { try { return JSON.parse(detail); } catch { return { text: detail }; } }
    return null;
  }

  /** Which community an entry is about, as its name: `site`, `site_id` or `communities` in the detail, or the target's prefix. */
  static #community(r) {
    const d = r.detail_obj ?? {};
    const sites = state.sites ?? [];
    const bySlug = (slug) => sites.find(s => s.slug === slug)?.name ?? slug;
    if (d.site) return bySlug(d.site);
    if (d.site_id) return sites.find(s => s.id === Number(d.site_id))?.name ?? `#${d.site_id}`;
    if (Array.isArray(d.communities) && d.communities.length) return d.communities.map(bySlug).join(', ');
    const head = String(r.target ?? '').split('/')[0];
    return sites.some(s => s.slug === head) ? bySlug(head) : '';
  }

  #render() {
    const n = this.#items.length;
    this.querySelector('arc-toolbar').count = `${n}${n >= LIMIT ? '+' : ''} entr${n === 1 ? 'y' : 'ies'}`;
    this.querySelector('arc-data-table').configure({
      columns: [
        { key: 'occurred_at', label: 'When', mono: true, render: r => esc(formatDateTime(r.occurred_at)) },
        { key: 'actor_login', label: 'Who', render: r => `<strong>${esc(r.actor_login ?? '')}</strong>` },
        { key: 'action', label: 'Did what', mono: true },
        { key: 'target', label: 'To', mono: true, clip: true, render: r => `<span title="${esc(r.target ?? '')}">${esc(r.target ?? '')}</span>` },
        { key: 'community', label: 'Community', clip: true, get: r => ArcAudit.#community(r), render: (r, v) => (v ? `<span title="${esc(v)}">${esc(v)}</span>` : '<span class="text-dim">—</span>') },
        { key: 'outcome', label: 'Result', render: r => badge(r.outcome === 'failed' ? 'failed' : 'ok') },
        { key: 'source_ip', label: 'From', mono: true },
        { key: 'detail', label: 'Detail', mono: true, fill: true, clip: true, render: r => { const t = ArcAudit.#detailText(r); return `<span title="${esc(t)}">${esc(t)}</span>`; } },
      ],
      rows: this.#items, rowKey: 'id', sort: null, limit: LIMIT, unit: 'entries',
      empty: { icon: 'audit', title: 'Nothing recorded', text: 'No privileged writes match these filters.' },
    });
  }

  /** What the Detail column says: the error if it failed, else the detail without the keys the other columns show. */
  static #detailText(r) {
    if (r.outcome === 'failed' && r.error) return r.error;
    const d = r.detail_obj;
    if (!d || typeof d !== 'object') return '';
    if (d.text) return d.text;
    const { site, site_id, self_service, ...rest } = d;   // eslint-disable-line no-unused-vars
    return Object.keys(rest).length ? JSON.stringify(rest) : '';
  }

  /** The whole entry, in the drawer: nothing is only in the cut-short column. */
  #open(r) {
    if (!r) return;
    const failed = r.outcome === 'failed';
    const detail = r.detail_obj && typeof r.detail_obj === 'object' ? JSON.stringify(r.detail_obj, null, 2) : '';
    const community = ArcAudit.#community(r);
    openInspector({
      title: r.action ?? 'Audit entry',
      width: 560,
      body: `
        <dl class="kv">
          <dt>When</dt><dd class="mono">${esc(formatDateTime(r.occurred_at))}</dd>
          <dt>Who</dt><dd><strong>${esc(r.actor_login ?? '')}</strong></dd>
          <dt>Did what</dt><dd class="mono">${esc(r.action ?? '')}</dd>
          <dt>To</dt><dd class="mono">${esc(r.target_type ? `${r.target_type}: ` : '')}${esc(r.target ?? '')}</dd>
          ${community ? `<dt>Community</dt><dd>${esc(community)}</dd>` : ''}
          <dt>Result</dt><dd>${badge(failed ? 'failed' : 'ok')}</dd>
          <dt>From</dt><dd class="mono">${esc(r.source_ip ?? '')}${r.surface ? ` <span class="text-muted">(${esc(r.surface)})</span>` : ''}</dd>
        </dl>
        ${failed && r.error ? `<h3>Error</h3><pre class="config-pre">${esc(r.error)}</pre>` : ''}
        ${detail ? `<h3>Detail</h3><pre class="config-pre">${esc(detail)}</pre>` : ''}`,
    });
  }
}

customElements.define('arc-audit', ArcAudit);
